import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Eye, Target } from "lucide-react";
import Seo from "../Components/Seo";
import PageHero from "../Components/PageHero";
import SectionHeading from "../Components/SectionHeading";
import StatCounter from "../Components/StatCounter";
import CTASection from "../Components/CTASection";
import { getIcon } from "../lib/icons";
import { CORE_VALUES, STATS } from "../data/site";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Seo
        title="About Triguna Solutions | Engineering Innovation Through Technology"
        description="Triguna Solutions helps businesses unlock growth through AI, software innovation, cloud transformation, and data intelligence. Learn about our mission, vision, and values."
        path="/about"
      />

      <PageHero
        eyebrow="About Us"
        title="Engineering Innovation Through Technology"
        subtitle="We are a team of engineers, designers, and strategists helping organizations build, scale, and reinvent what's possible with technology."
      />

      {/* Mission & Vision */}
      <section className="px-4 pt-20 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5 }}
            className="rounded-3xl border border-border bg-card p-8"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-gradient text-white">
              <Target className="h-6 w-6" />
            </span>
            <h2 className="mt-5 font-display text-2xl font-bold text-foreground">Our Mission</h2>
            <p className="mt-3 text-muted-foreground">
              To help businesses unlock growth through AI, software innovation,
              cloud transformation, and data intelligence.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-3xl border border-border bg-card p-8"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-gradient text-white">
              <Eye className="h-6 w-6" />
            </span>
            <h2 className="mt-5 font-display text-2xl font-bold text-foreground">Our Vision</h2>
            <p className="mt-3 text-muted-foreground">
              To become a trusted global technology partner for digital
              transformation — delivering measurable outcomes at every scale.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-8 rounded-3xl border border-border bg-card/50 px-6 py-12 lg:grid-cols-4">
          {STATS.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="font-display text-4xl font-bold text-foreground sm:text-5xl">
                <StatCounter value={stat.value} suffix={stat.suffix} />
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-card/40 px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="What Drives Us"
            title="Our Core Values"
            subtitle="The principles behind every project we take on."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {CORE_VALUES.map((value, i) => {
              const Icon = getIcon(value.icon);
              return (
                <motion.div
                  key={value.title}
                  {...fadeUp}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="rounded-2xl border border-border bg-card p-6 text-center"
                >
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-4 font-display text-base font-semibold text-foreground">
                    {value.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection
        title="Let's build something extraordinary together"
        text="Partner with a team that treats your goals as our own."
        buttonLabel="Schedule Consultation"
      />
    </>
  );
};

export default AboutPage;
